import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Copy, Eye, EyeOff } from 'lucide-react';
import { usersApi } from '@/api/users';
import { Button, Modal } from '@/components/ui';
import { cn } from '@/utils/helpers';
import type { ResetMode } from '@/types';

/**
 * Minimal shape the modal needs. Both `OrgMember` (Members tab) and a row from
 * the global Users page can be passed as long as they expose these fields.
 */
export interface ResetTarget {
  auth_user_id: string | null;
  name?: string | null;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
}

interface Props {
  member: ResetTarget | null;
  isOpen: boolean;
  onClose: () => void;
}

const modes: { id: ResetMode; label: string; description: string }[] = [
  { id: 'direct', label: 'Set password manually', description: 'Enter a new password — takes effect immediately' },
  { id: 'auto', label: 'Auto-generate password', description: 'Generate a strong password, show once, and email the user' },
  { id: 'magic_link', label: 'Send recovery link', description: 'User sets their own password via email link' },
];

function memberDisplayName(member: ResetTarget | null): string {
  if (!member) return 'User';
  if (member.name?.trim()) return member.name.trim();
  const full = [member.first_name, member.last_name].filter(Boolean).join(' ').trim();
  return full || member.email;
}

export default function ResetPasswordModal({ member, isOpen, onClose }: Props) {
  const [mode, setMode] = useState<ResetMode>('direct');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setMode('direct');
      setPassword('');
      setShowPassword(false);
      setGeneratedPassword(null);
    }
  }, [isOpen]);

  const resetMutation = useMutation({
    mutationFn: () => {
      if (!member?.auth_user_id) throw new Error('User has no auth account');
      return usersApi.resetPassword({
        target_auth_user_id: member.auth_user_id,
        mode,
        ...(mode === 'direct' ? { new_password: password } : {}),
      });
    },
    onSuccess: (result) => {
      if (result.mode === 'auto' && result.generated_password) {
        setGeneratedPassword(result.generated_password);
        setPassword('');
      } else if (result.mode === 'direct') {
        setPassword('');
        toast.success('Password updated');
        onClose();
      } else if (result.mode === 'magic_link') {
        toast.success('Password updated');
        onClose();
      }

      if (result.warning) {
        toast.error(result.warning);
      }
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Password reset failed');
    },
  });

  const copyGenerated = async () => {
    if (!generatedPassword) return;
    try {
      await navigator.clipboard.writeText(generatedPassword);
      toast.success('Password copied');
    } catch {
      toast.error('Could not copy password');
    }
  };

  const displayName = memberDisplayName(member);
  const canSubmit = mode !== 'direct' || password.length >= 8;

  return (
    <>
      <Modal
        isOpen={isOpen && !generatedPassword}
        onClose={onClose}
        title="Reset password"
        footer={
          <>
            <Button variant="secondary" onClick={onClose} disabled={resetMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => resetMutation.mutate()}
              loading={resetMutation.isPending}
              disabled={!canSubmit || !member?.auth_user_id}
            >
              {mode === 'direct' ? 'Save' : mode === 'auto' ? 'Generate & send' : 'Send link'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-ink-secondary">
            Reset password for <span className="font-medium text-ink-primary">{displayName}</span>
          </p>

          {!member?.auth_user_id ? (
            <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-300">
              This user has no linked auth account — password reset is unavailable.
            </p>
          ) : (
            <>
              <div className="space-y-2">
                {modes.map((item) => (
                  <label
                    key={item.id}
                    className={cn(
                      'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                      mode === item.id
                        ? 'border-gold/40 bg-gold/5'
                        : 'border-navy-border hover:border-navy-border/80',
                    )}
                  >
                    <input
                      type="radio"
                      name="reset-password-mode"
                      checked={mode === item.id}
                      onChange={() => setMode(item.id)}
                      className="mt-1"
                    />
                    <div>
                      <p className="text-sm font-medium text-ink-primary">{item.label}</p>
                      <p className="text-xs text-ink-secondary">{item.description}</p>
                    </div>
                  </label>
                ))}
              </div>

              {mode === 'direct' && (
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="New password (min 8 characters)"
                    minLength={8}
                    dir="ltr"
                    className="w-full rounded-lg border border-navy-border bg-navy-card px-3 py-2 pe-10 text-sm text-ink-primary"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute inset-y-0 end-0 inline-flex w-10 items-center justify-center text-ink-muted hover:text-ink-primary"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={!!generatedPassword}
        onClose={() => {
          setGeneratedPassword(null);
          onClose();
        }}
        title="New password generated"
        footer={
          <Button
            onClick={() => {
              setGeneratedPassword(null);
              onClose();
            }}
          >
            Close
          </Button>
        }
      >
        <div className="space-y-3 text-sm text-ink-secondary">
          <p>A new password was generated and emailed to the user.</p>
          <div className="flex items-center gap-2 rounded-lg border border-navy-border bg-navy-dark px-3 py-2">
            <span className="flex-1 font-mono text-sm text-ink-primary" dir="ltr">
              {generatedPassword}
            </span>
            <button
              type="button"
              onClick={() => void copyGenerated()}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-gold hover:bg-gold/10"
            >
              <Copy className="h-3.5 w-3.5" />
              Copy
            </button>
          </div>
          <p className="text-xs text-ink-muted">Shown once only — not stored after you close this dialog.</p>
        </div>
      </Modal>
    </>
  );
}
